import * as React from 'react'
import PropTypes from 'prop-types'
import {
  BaseHeading,
  HeadingDisplayLevel as BaseHeadingDisplayLevel,
  headingDisplayLevels,
  HeadingLevel,
  headingLevels,
  HeadingTarget,
} from '../../internal/components/BaseHeading'
import { useDsUiDataProps } from '../../theming'
import { CustomPropTypes } from '../../support'
import type { ButtonOnClickHandler, HeadingForwardRef } from '../../types'
import { AnchorTarget, anchorTargets } from '../../variables'
import type { TextEllipsisProps } from '../TextEllipsis/TextEllipsis'

export { headingLevels }
export type { HeadingLevel }

export type HeadingDisplayLevel = Exclude<
  BaseHeadingDisplayLevel,
  'section-headline' | 'body-m'
>
export { headingDisplayLevels }

export type { HeadingTarget }

export interface HeadingProps {
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * An optional display level (i.e. importance indicated by size / style / casing)
   * that may differ from the semantic HTML section heading level.
   */
  displayLevel?: HeadingDisplayLevel
  /**
   * A React ref to assign to the HTML node representing the Heading element.
   */
  forwardedRef?: HeadingForwardRef
  /**
   * If href is provided then it will render an anchor, otherwise a button.
   */
  href?: string
  /**
   * A unique string identifier attribute for the Heading component.
   */
  id?: string
  /**
   * The semantic HTML section heading level.
   * Headings are important for assistive technology users. Heading levels should be sequential and not skip levels.
   * See the [Semantic Headings accessibility documentation](https://docusign.atlassian.net/wiki/display/ACC/Semantic+Headings) for more info.
   */
  level: HeadingLevel
  /**
   * The function to call when a 'click' event is fired.
   */
  onClick?: ButtonOnClickHandler
  /**
   * The relationship of the linked URL of a anchor as space-separated link types.
   *
   * Reference: https://developer.mozilla.org/en-US/docs/Web/HTML/Link_types
   */
  rel?: string
  /**
   * Sets tab order for the heading element.
   */
  tabIndex?: number
  /**
   * The HTML link target (if an href is _not_ provided, this is ignored).
   * When target="_blank" use rel="noreferrer" or rel="noopener" to avoid the vulnerability.
   */
  target?: AnchorTarget
  /**
   * The text to be displayed.
   */
  text: string | React.ReactElement<TextEllipsisProps>
}

export function Heading({
  displayLevel,
  forwardedRef,
  href,
  id,
  level,
  onClick,
  rel,
  tabIndex,
  target,
  text,
  ...restProps
}: HeadingProps) {
  const dsUiDataProps = useDsUiDataProps(Heading)
  return (
    <BaseHeading
      {...dsUiDataProps}
      {...restProps}
      displayLevel={displayLevel}
      forwardedRef={forwardedRef}
      href={href}
      id={id}
      level={level}
      onClick={onClick}
      rel={rel}
      tabIndex={tabIndex}
      target={target}
      text={text}
    />
  )
}

Heading.propTypes = {
  'data-.*': PropTypes.string,
  displayLevel: PropTypes.oneOf(headingDisplayLevels),
  forwardedRef: CustomPropTypes.ReactRef,
  href: PropTypes.string,
  id: PropTypes.string,
  level: PropTypes.oneOf(headingLevels).isRequired,
  'on[A-Z].*': PropTypes.func,
  onClick: PropTypes.func,
  rel: PropTypes.string,
  tabIndex: PropTypes.number,
  target: PropTypes.oneOf(anchorTargets),
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
}

Heading.displayName = 'Heading'
